import React, { useState } from 'react';
import { Card, Table, Input, Select, Space, Tag, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import styles from './System.module.scss';

const { Search } = Input;
const { Option } = Select;

const ProcessList = ({ processList }) => {
  const [, setSearchText] = useState('');
  const [filteredProcesses, setFilteredProcesses] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [sortField, setSortField] = useState('cpu');
  const [sortOrder, setSortOrder] = useState('descend');

  // 프로세스 목록 파싱
  const parseProcessList = (data) => {
    if (!data || data === 'N/A') return [];
    
    try {
      const processes = [];
      const lines = data.trim().split('\n');
      
      // 헤더 라인 건너뛰기
      const dataLines = lines.slice(1);
      
      dataLines.forEach(line => {
        const parts = line.trim().split(/\s+/);
        if (parts.length >= 11) {
          const user = parts[0];
          const pid = parts[1];
          const cpu = parseFloat(parts[2]);
          const mem = parseFloat(parts[3]);
          const vsz = parts[4];
          const rss = parts[5];
          const tty = parts[6];
          const stat = parts[7];
          const start = parts[8];
          const time = parts[9];
          const command = parts.slice(10).join(' ');
          
          processes.push({
            user,
            pid,
            cpu,
            mem,
            vsz,
            rss,
            tty,
            stat,
            start,
            time,
            command,
            key: pid
          });
        }
      });
      
      return processes;
    } catch (error) {
      console.error('Error parsing process list:', error);
      return [];
    }
  };

  const processes = parseProcessList(processList);

  const handleSearch = (value) => {
    setSearchText(value);
    if (value) {
      const filtered = processes.filter(process => 
        process.command.toLowerCase().includes(value.toLowerCase()) ||
        process.user.toLowerCase().includes(value.toLowerCase()) ||
        process.pid.includes(value)
      );
      setFilteredProcesses(filtered);
      setIsFiltered(true);
    } else {
      setFilteredProcesses([]);
      setIsFiltered(false);
    }
  };

  const handleSortChange = (value) => {
    setSortField(value);
  };

  const handleOrderChange = (value) => {
    setSortOrder(value);
  };

  const getStatusColor = (stat) => {
    if (stat.includes('R')) return 'green';
    if (stat.includes('S')) return 'blue';
    if (stat.includes('D')) return 'red';
    if (stat.includes('Z')) return 'orange';
    if (stat.includes('T')) return 'purple';
    return 'default';
  };

  const sortedProcesses = [...(isFiltered ? filteredProcesses : processes)].sort((a, b) => {
    if (sortOrder === 'ascend') {
      return a[sortField] - b[sortField];
    } else {
      return b[sortField] - a[sortField];
    }
  });

  return (
    <Card 
      title={
        <div>
          프로세스 목록
          <Tooltip title="시스템에서 실행 중인 프로세스 목록입니다.">
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
          </Tooltip>
        </div>
      } 
      className={styles.systemCard}
      extra={
        <Space>
          <Search
            placeholder="프로세스 검색..."
            allowClear
            onSearch={handleSearch}
            style={{ width: 200 }}
          />
          <Select 
            defaultValue="cpu" 
            style={{ width: 120 }} 
            onChange={handleSortChange}
          >
            <Option value="cpu">CPU 사용률</Option>
            <Option value="mem">메모리 사용률</Option>
            <Option value="pid">PID</Option>
          </Select>
          <Select 
            defaultValue="descend" 
            style={{ width: 120 }} 
            onChange={handleOrderChange}
          >
            <Option value="descend">내림차순</Option>
            <Option value="ascend">오름차순</Option>
          </Select>
        </Space>
      }
    >
      <Table 
        dataSource={sortedProcesses}
        rowKey="key"
        pagination={{ pageSize: 10 }}
        columns={[
          { title: 'PID', dataIndex: 'pid', key: 'pid', width: 80 },
          { title: '사용자', dataIndex: 'user', key: 'user', width: 100 },
          { 
            title: 'CPU %', 
            dataIndex: 'cpu', 
            key: 'cpu', 
            width: 80,
            sorter: (a, b) => a.cpu - b.cpu,
            defaultSortOrder: 'descend'
          },
          { 
            title: '메모리 %', 
            dataIndex: 'mem', 
            key: 'mem', 
            width: 80,
            sorter: (a, b) => a.mem - b.mem
          },
          { title: 'VSZ', dataIndex: 'vsz', key: 'vsz', width: 80 },
          { title: 'RSS', dataIndex: 'rss', key: 'rss', width: 80 },
          { 
            title: '상태', 
            dataIndex: 'stat', 
            key: 'stat', 
            width: 80,
            render: stat => <Tag color={getStatusColor(stat)}>{stat}</Tag>
          },
          { title: '시작 시간', dataIndex: 'start', key: 'start', width: 100 },
          { title: '실행 시간', dataIndex: 'time', key: 'time', width: 100 },
          { 
            title: '명령어', 
            dataIndex: 'command', 
            key: 'command',
            ellipsis: true
          }
        ]}
      />
    </Card>
  );
};

export default ProcessList;